import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Lock, Loader2, CheckCircle } from 'lucide-react';
import { API } from '../lib/constants';

const ResetPassword = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    if (password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (password !== confirm) { toast.error('Passwords do not match'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/reset-password`, { token, new_password: password });
      setDone(true);
      toast.success('Password updated!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <div className="w-14 h-14 bg-[#1a1a1a] rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-[#16A34A] font-black text-2xl">O</span>
          </div>
          <h1 className="text-3xl font-black mb-2">Set a new password</h1>
          <p className="text-white/50">Choose a new password for your account.</p>
        </div>

        {!token ? (
          <div className="text-center text-white/60" data-testid="reset-no-token">
            <p>This reset link is invalid or incomplete.</p>
            <Button onClick={() => navigate('/forgot-password')} className="mt-5 bg-[#0F5132] hover:bg-[#0B3B24] text-white font-bold h-12 rounded-xl">Request a new link</Button>
          </div>
        ) : done ? (
          <div className="text-center space-y-5" data-testid="reset-done-message">
            <CheckCircle className="h-14 w-14 text-[#16A34A] mx-auto" />
            <p className="text-white/80">Your password has been updated. You can now sign in.</p>
            <Button onClick={() => navigate('/signin')} className="w-full bg-[#0F5132] hover:bg-[#0B3B24] text-white font-bold h-12 rounded-xl" data-testid="reset-go-signin-btn">
              Go to Sign In
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-semibold text-white/70 flex items-center gap-2">
                <Lock className="h-4 w-4" /> New password
              </label>
              <Input
                value={password}
                onChange={e => setPassword(e.target.value)}
                type="password"
                placeholder="At least 6 characters"
                data-testid="reset-password-input"
                className="bg-white/5 border-white/10 text-white h-12 rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-white/70 flex items-center gap-2">
                <Lock className="h-4 w-4" /> Confirm password
              </label>
              <Input
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                type="password"
                placeholder="Re-enter password"
                data-testid="reset-confirm-input"
                onKeyPress={e => e.key === 'Enter' && submit()}
                className="bg-white/5 border-white/10 text-white h-12 rounded-xl"
              />
            </div>
            <Button onClick={submit} disabled={!password || !confirm || loading} data-testid="reset-submit-btn"
              className="w-full bg-[#0F5132] hover:bg-[#0B3B24] text-white font-bold h-12 rounded-xl text-lg">
              {loading ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : null}
              Update Password
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
